import { Field, FieldProps, Form, Formik, useFormikContext } from "formik";
import { Divider, TextField, Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import * as Yup from "yup";
import colors from "assets/theme-dark/base/colors";
import { styled } from "@mui/system";
import { CRUXY_ADMIN, CRUXY_USER } from "common/names";
import { ClientsResponse, User } from "graphql/types/graphql";
import NewUserForm from "./NewUsersForm";
import CubeManagementForm from "./CubeManagementForm";

interface Cube {
  cubeId: string;
  name: string;
}

interface FormValue {
  cruxyId: string;
  role: string;
  email: string;
  password: string;
  repeatPassword: string;
}

interface CubeFormValue {
  name: string;
}

interface CompanyFormValue {
  companyId: string;
  companyName: string;
}

interface FormsCardProps {
  user: any;
  companies: ClientsResponse[];
  roles: string[];
  cubes: Cube[] | undefined;
  selectedCubeId: string | null;
  errorEmail: boolean;
  errorRepeatPass: boolean;
  errorPass: boolean;
  errorUser: string;
  errorCompany: string;
  errorCompanyId: boolean;
  errorCompanyName: boolean;
  errorCubeName: boolean;
  cubesLoading: boolean;
  deleteLoading: boolean;
  setErrorEmail: (error: boolean) => void;
  setErrorRepeatPass: (error: boolean) => void;
  setErrorPass: (error: boolean) => void;
  setErrorUser: (error: string) => void;
  setErrorCompany: (error: string) => void;
  setErrorCompanyId: (error: boolean) => void;
  setErrorCompanyName: (error: boolean) => void;
  setErrorCubeName: (error: boolean) => void;
  setSelectedCubeId: (cubeId: string | null) => void;
  setOpenDeleteDialog: (open: boolean) => void;
  signUpHandler: (values: FormValue) => Promise<void>;
  newCubeHandler: (values: CubeFormValue, { resetForm }: { resetForm: () => void }) => Promise<void>;
}

const newCompanySchema = Yup.object().shape({
  companyId: Yup.string()
    .required("CruxyId required!")
    .test("", "Company ID must not contain spaces", function (value) {
      return !/\s/.test(value);
    }),
  companyName: Yup.string().required("Name required!"),
});

const FormsStyledCard = styled(MDBox)`
  width: 50%;
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

function ErrorCompanyTypography(props: { errorCompanyId: boolean; errorCompanyName: boolean }): JSX.Element {
  const { errorCompanyId, errorCompanyName } = props;
  const context = useFormikContext<CompanyFormValue>();
  const item = context.errors;

  if (Object.keys(item).length === 0) {
    return (
      <MDTypography mt={2} color="error">
        {" "}
      </MDTypography>
    );
  }
  if (item?.companyId && errorCompanyId) {
    return (
      <MDTypography mt={2} color="error">
        {item.companyId}
      </MDTypography>
    );
  }
  if (item?.companyName && errorCompanyName) {
    return (
      <MDTypography mt={2} color="error">
        {item.companyName}
      </MDTypography>
    );
  }
  return (
    <MDTypography mt={2} color="error">
      {" "}
    </MDTypography>
  );
}

function FormsCard({
  user,
  companies,
  roles,
  cubes,
  selectedCubeId,
  errorEmail,
  errorRepeatPass,
  errorPass,
  errorUser,
  errorCompany,
  errorCompanyId,
  errorCompanyName,
  errorCubeName,
  cubesLoading,
  deleteLoading,
  setErrorEmail,
  setErrorRepeatPass,
  setErrorPass,
  setErrorUser,
  setErrorCompany,
  setErrorCompanyId,
  setErrorCompanyName,
  setErrorCubeName,
  setSelectedCubeId,
  setOpenDeleteDialog,
  signUpHandler,
  newCubeHandler,
}: FormsCardProps): JSX.Element {
  return (
    <FormsStyledCard>
      <NewUserForm
        user={user}
        companies={companies}
        roles={roles}
        errorEmail={errorEmail}
        errorRepeatPass={errorRepeatPass}
        errorPass={errorPass}
        errorUser={errorUser}
        setErrorEmail={setErrorEmail}
        setErrorRepeatPass={setErrorRepeatPass}
        setErrorPass={setErrorPass}
        setErrorUser={setErrorUser}
        signUpHandler={signUpHandler}
      />

      <Divider sx={{ mt: 4 }} />

      {/* <Typography
        sx={{
          color: colors.white.main,
          fontSize: "1.5rem",
          fontWeight: 600,
          marginBottom: "1rem",
        }}
      >
        New Company
      </Typography>
      <Formik
        initialValues={{
          cruxyId: "",
          name: "",
        }}
        validationSchema={newCompanySchema}
        onSubmit={async (values) => {
          await newClientHandler({ companyId: values.cruxyId, companyName: values.name });
        }}
      >
        <Form style={{ width: "100%" }}>
          {Boolean(user.role === CRUXY_ADMIN || user.role === CRUXY_USER) && (
            <>
              <Field sx={{ width: "100%" }} name="companyId">
                {(props: FieldProps<string, FormValue>) => (
                  <TextField
                    size="small"
                    onBlur={() => setErrorCompanyId(true)}
                    inputProps={{ "data-testid": "companyIdForm" }}
                    sx={{ width: "100%", marginBottom: "1.5rem" }}
                    name="Company ID"
                    label={"Company ID"}
                    color="secondary"
                    autoFocus
                    required
                    value={props.field.value}
                    onChange={(e) => {
                      setErrorCompanyId(false);
                      setErrorCompany(" ");
                      props.form.setFieldValue(props.field.name, e.target.value, true);
                    }}
                  />
                )}
              </Field>
              <Field sx={{ width: "100%" }} name="companyName">
                {(props: FieldProps<string, FormValue>) => (
                  <TextField
                    size="small"
                    onBlur={() => setErrorCompanyName(true)}
                    inputProps={{ "data-testid": "companyNameForm" }}
                    sx={{ width: "100%", marginBottom: "1.5rem" }}
                    name="Company Name"
                    label={"Company Name"}
                    color="secondary"
                    autoFocus
                    required
                    value={props.field.value}
                    onChange={(e) => {
                      setErrorCompanyName(false);
                      setErrorCompany(" ");
                      props.form.setFieldValue(props.field.name, e.target.value, true);
                    }}
                  />
                )}
              </Field>
            </>
          )}

          <ErrorCompanyTypography errorCompanyId={errorCompanyId} errorCompanyName={errorCompanyName} />

          <MDBox mb={1}>
            <MDButton type="submit" variant="gradient" color="secondary" size="large" fullWidth>
              Add Company
            </MDButton>
          </MDBox>
        </Form>
      </Formik> */}

      <CubeManagementForm
        user={user}
        cubes={cubes}
        selectedCubeId={selectedCubeId}
        errorCubeName={errorCubeName}
        cubesLoading={cubesLoading}
        deleteLoading={deleteLoading}
        setErrorCubeName={setErrorCubeName}
        setErrorCompany={setErrorCompany}
        setSelectedCubeId={setSelectedCubeId}
        setOpenDeleteDialog={setOpenDeleteDialog}
        newCubeHandler={newCubeHandler}
      />
    </FormsStyledCard>
  );
}

export default FormsCard;
